// Phase 39 — in-app WebRTC media layer for internal calls (1-to-1 + small ad-hoc
// groups, ≤4 total). Full mesh: each participant holds one RTCPeerConnection to
// each other participant — no media server. Signalling rides the existing hub
// (recipient-exact ephemeral push) backed by a durable pull mailbox, so a
// dropped signalling blob never silently fails the call.
//
// Design notes:
//  • Negotiation: the canonical "perfect negotiation" pattern. The higher
//    collaborator id is POLITE (rolls back on glare); the lower is impolite.
//  • Apply path (Phase 42, Lever A): the recipient-exact ephemeral push now
//    carries the SDP/ICE blob AND is applied directly (onPush → applySignal),
//    so the happy path costs no extra GET and no 40 ms debounce. The durable
//    ordered pull (GET /calls/:id/signals?after=<rowId>) remains as the
//    reconnect / dropped-push / multi-process BACKSTOP: it advances the `cursor`
//    watermark contiguously in id order and de-dupes anything the push already
//    applied (via `appliedIds`), so push-applied and pull-applied signals can
//    never double-apply or drop. All applies are serialized through one chain
//    so a push-apply can't race a pull-apply on the same PC.
//  • The mesh is driven by the authoritative call roster: App computes who is
//    "in" the call from each persistent 'call' event and calls setPeers(). Phase
//    42 (Lever C) splits that into MESH targets (peers we hold a media-less PC
//    to — incl. ringing invitees, for ring-time pre-warm) and ARMED targets
//    (peers we actually SEND media to — only once both ends are committed).
//    ensurePeer creates sendrecv transceivers with NO track; media is injected
//    later by armPeer via replaceTrack (no renegotiation) — so a pre-warmed PC
//    reaches `connected` during ring carrying zero media, and accept only flips
//    the streams on. The privacy invariant: no track is ever added outside
//    armPeer, and armPeer only runs for committed peers.
//
// This module is transport-only; the React call window renders from getState()
// via the onChange callback.

/* global navigator */
import { api } from "../api";
import { callPerfMark, callPerfFlush, callDiagEnabled } from "./callPerf";
import { resolveCallDeviceIds } from "./callDevices"; // 204 §9 — the remembered microphone and camera

export function classifyMediaError(e: any) {
  const n = e && e.name;
  if (n === "NotAllowedError" || n === "SecurityError" || n === "PermissionDeniedError") return "denied";
  if (n === "NotFoundError" || n === "DevicesNotFoundError" || n === "OverconstrainedError") return "no-device";
  if (n === "NotReadableError" || n === "TrackStartError" || n === "AbortError") return "in-use";
  return "error";
}

// Acquire camera+mic with graceful degrade. Tied to a user gesture by the
// caller (the click that starts/accepts the call). Falls back to audio-only if
// the camera is blocked/absent; reports the failure mode when nothing is
// available so the UI can explain it.
// 204 §9 — WITH THE REMEMBERED DEVICES: `userId` names whose browser-stored
// choice to restore (lib/callDevices.ts — the id if still present, else the
// device with the stored label, else the default). The exact constraints are
// asked for FIRST; if the device has gone since the enumeration (unplugged
// between the two calls) the request throws and the ordinary default path
// below runs — the store is never cleared by an absence.
export async function acquireLocalMedia(opts?: { userId?: unknown }) {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { stream: null, hasVideo: false, hasAudio: false, error: "unsupported" };
  }
  const VIDEO = { width: { ideal: 1280 }, height: { ideal: 720 } };
  const chosen = opts && opts.userId != null ? await resolveCallDeviceIds(opts.userId) : { audioDeviceId: null, videoDeviceId: null };
  if (chosen.audioDeviceId || chosen.videoDeviceId) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: chosen.audioDeviceId ? { deviceId: { exact: chosen.audioDeviceId } } : true,
        video: chosen.videoDeviceId ? { deviceId: { exact: chosen.videoDeviceId }, ...VIDEO } : VIDEO,
      });
      return { stream, hasVideo: stream.getVideoTracks().length > 0, hasAudio: stream.getAudioTracks().length > 0 };
    } catch {
      /* the remembered device is not usable right now — the default path below, the store untouched */
    }
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: VIDEO,
    });
    return { stream, hasVideo: stream.getVideoTracks().length > 0, hasAudio: stream.getAudioTracks().length > 0 };
  } catch (e1) {
    // Camera denied/absent/busy — try audio-only so the call still works.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return { stream, hasVideo: false, hasAudio: true, degraded: classifyMediaError(e1) };
    } catch (e2) {
      return { stream: null, hasVideo: false, hasAudio: false, error: classifyMediaError(e2) };
    }
  }
}

// Fetch short-lived ICE/TURN servers from the API (empty on staging without a
// relay — same-network peers still connect on host candidates). Phase 42: cached
// within ~90% of the credential TTL (default 1h) so a second call, or the accept
// leg after a ring-time pre-fetch, skips the round-trip — the HMAC creds embed
// their own expiry and stay valid across the window.
let _iceCache: { iceServers: any[]; expiresAt: number } | null = null; // { iceServers, expiresAt }
export async function fetchIceServers() {
  const nowMs = Date.now();
  if (_iceCache && _iceCache.expiresAt > nowMs) return _iceCache.iceServers;
  try {
    const r = (await api.get("/calls/turn-credentials")) as {
      iceServers?: RTCIceServer[];
      ttl?: number;
    } | null;
    const iceServers = r && Array.isArray(r.iceServers) ? r.iceServers : [];
    const ttl = r && Number(r.ttl) > 0 ? Number(r.ttl) : 3600;
    _iceCache = { iceServers, expiresAt: nowMs + Math.floor(ttl * 900) }; // ttl * 1000 * 0.9
    return iceServers;
  } catch {
    return _iceCache ? _iceCache.iceServers : [];
  }
}

// 70 §11bis — the fields of RTCStats `type: "transport"` this diagnostic reads.
// Not in the DOM lib at this TypeScript version; every field optional, because a
// browser that does not report one must produce `undefined` and not a throw.
type TransportStat = {
  type?: string;
  dtlsState?: string;
  dtlsRole?: string;
  iceState?: string;
  selectedCandidatePairId?: string;
  srtpCipher?: string;
  tlsVersion?: string;
};

/* 72 — THE SHARE LEG'S TWO SHAPES, AND THEY LIVE HERE BECAUSE BOTH READERS ARE
   OUTSIDE. A screen share is a SECOND call session on its own callId (brief 72
   §2, ruled), so `App` builds one and `CallWindow` renders it, and a type
   declared in either of those two would have to be duplicated in the other.
   This module owns call-session shapes, so it owns these.
   They are DECLARED rather than left as `any` for the reason
   scripts/lint/ratchets.js exists: `TransportStat` above is the same move for
   the same reason. */

/** What `callService.shapeLeg` publishes on the 'callLeg' event family. */
export type ShareLeg = {
  id: number;
  parentCallId: number;
  sharerId: number;
  startedAt: string;
  endedAt: string | null;
  participantIds: number[];
};

/** The fields of a leg session's `getState()` snapshot that anything reads.
 *  Narrower than the conversation's view on purpose: a leg has no microphone,
 *  no camera and no media error — one video track, in one direction. */
export type ShareView = {
  localStream: MediaStream | null;
  /* 74o (a) — IDENTITY. The capturer's thumbnail must render the EXACT track the
     outgoing sender is transmitting, reached FROM THE SENDER — not a lookup, not a
     re-acquired stream, not the first display track found. `localStream` is what the
     session was HANDED; `sentStream` is what a sender actually holds, wrapped once
     per track identity. They are the same object in the happy path and nothing used
     to enforce it, which is exactly the gap where a stale share's track could be on
     screen while a newer one was on the wire. */
  sentStream: MediaStream | null;
  /* 74y — WHICH SESSION OBJECT THIS SNAPSHOT CAME FROM. 74o asked which TRACK a
     surface holds; the case it could not see is one level up — the right track
     reached through a STALE SESSION, whose snapshot is still wired into the UI
     while the leg on the wire belongs to a newer one. A track id alone cannot
     tell those apart, because a stale session's track id may be perfectly valid.
     Monotonic per page load, no clock and no randomness, so it is stable for any
     recording that captures it. */
  sessionTag: string;
  /* 73 §2 — the two failing shapes ride the snapshot, because the transport is
     what observes them: `stalled` is 70 §11bis's ICE-connected-DTLS-incomplete,
     `mediaSilent` is this brief's connected-and-carrying-nothing. A leg needs
     both to tell a person which of the two happened rather than showing a
     waiting message forever. */
  peers: { id: number; stream: MediaStream | null; stalled?: boolean; mediaSilent?: boolean }[];
};

let _sessionSeq = 0;

export function createCallSession({ callId, selfId, localStream, iceServers, onChange, expectRemoteMedia = true }: {
  callId: any;
  selfId: any;
  localStream?: MediaStream | null;
  iceServers?: any[];
  onChange?: (state: any) => void;
  /* 73 §2 — DO WE EXPECT MEDIA BACK FROM EACH PEER? `true` for every caller that
     existed before this flag, because a conversation is symmetric: everybody
     sends. A screen-share leg is NOT — its sharer sends and receives nothing, so
     a sharer whose peers never fire `ontrack` is working correctly, and a watch
     that flagged it would cry wolf on the one session most likely to be looked
     at. The flag is `false` for exactly that case. Union, don't select: the
     default preserves every existing call site's behaviour. */
  expectRemoteMedia?: boolean;
}) {
  const self = Number(selfId);
  const peers = new Map<number, any>(); // peerId -> { id, pc, stream, state, polite, makingOffer, ignoreOffer, pendingIce, failTimer, senders, armed, wantArm }
  let stream: MediaStream | null = localStream || null;
  let micOn = !!(stream && stream.getAudioTracks().some((t) => t.enabled));
  let camOn = !!(stream && stream.getVideoTracks().some((t) => t.enabled));
  let destroyed = false;
  let cursor = 0; // last CONTIGUOUSLY-pulled signal row id (durable watermark)
  const appliedIds = new Set<number>(); // ids applied out-of-band via push, ahead of cursor
  let pulling = false;
  let pullAgain = false;
  let pullTimer: any = null;
  let ticks = 0;
  let applyChain = Promise.resolve(); // serializes all applySignal (push + pull)
  let perfTrackMarked = false;
  let perfIceMarked = false;
  const seqByPeer = new Map<number, number>(); // peerId -> my outgoing per-edge seq
  const config = { iceServers: Array.isArray(iceServers) ? iceServers : [] };

  const emit = () => { if (!destroyed && onChange) onChange(getState()); };

  /* ── 74o (a) — WHAT THE SENDER IS ACTUALLY TRANSMITTING ──────────────────────
     Reached from the RTCRtpSender, so the thing rendered and the thing on the wire
     cannot be two different objects. Wrapped in a MediaStream because that is what a
     <video> takes, and rebuilt ONLY when the track's identity changes — a new stream
     object on every emit would reset `srcObject` and flicker the picture.
     AND IT CARRIES THE IDENTITY CHECK PERMANENTLY. If the sender's track is ever not
     the one the session was handed, that is the whole of the bug 74o (a) describes,
     and one unconditional line says so with both ids — which is the log Laurent
     asked for, available on every future occurrence rather than only while somebody
     is looking. */
  const sessionTag = `s${++_sessionSeq}`;
  let sentMirror: MediaStream | null = null;
  let sentMirrorId: string | null = null;
  const senderVideoTrack = () => {
    for (const p of peers.values()) {
      const t = p.senders.video && p.senders.video.track;
      if (t) return t as MediaStreamTrack;
    }
    return null;
  };
  const refreshSentMirror = () => {
    const t = senderVideoTrack();
    const id = t ? t.id : null;
    if (id === sentMirrorId) return;
    sentMirrorId = id;
    sentMirror = t ? new MediaStream([t]) : null;
    /* 74y — 74o's IDENTITY MISMATCH WARNING WAS REMOVED FROM HERE, and its removal
       is the finding rather than a tidy-up. It compared the sender's track against
       THIS SAME SESSION's local stream — but `startShare` builds a fresh session per
       share whose `localStream` IS the capture the sender holds, and `setLocalMedia`
       is never called on a share session, so the two operands were equal BY
       CONSTRUCTION and the warning could not fire whether the picture was right or
       wrong. A check that cannot fire is worse than no check: its silence reads as a
       result. The comparand has to survive across sessions, so it cannot live in
       here at all — it is the picker's own recorded track id, held by the component
       that called `getDisplayMedia`, and the discriminator is in `CallWindow`. */
  };

  // ── Phase 42 — call diagnostics (gated by lx:callperf, OFF by default) ───────
  // Retained after the same-network-stall fix: a per-peer state dump every ~2s +
  // gated logging of benign catches (addIceCandidate for late/ignored candidates,
  // sendSignal delivery). LOGGING ONLY — with the flag off, every helper below is
  // a no-op. NOTE: the two NEGOTIATION-CRITICAL catches (onnegotiationneeded +
  // applySignal) warn UNCONDITIONALLY (`[lxcall …]`), so a future silent
  // negotiation failure can't hide the way this deadlock did — those three
  // original silent `catch {}` blocks were the reason it took a full cycle to see.
  const diagWarn = (...a: any[]) => { if (callDiagEnabled()) console.warn(`[calldiag ${callId}]`, ...a); };
  const diagLog = (...a: any[]) => { if (callDiagEnabled()) console.log(`[calldiag ${callId}]`, ...a); };
  const mLineCount = (d: any) => (d && d.sdp ? (d.sdp.match(/^m=/gm) || []).length : null);
  // ── 70 §11bis — THE STATE THAT WAS STUCK WAS NOT AMONG THE FOUR PRINTED ─────
  // The 2026-08-11 capture showed `iceConnectionState: 'connected'` and
  // `iceGatheringState: 'complete'` beside `connectionState: 'connecting'`, held
  // for nine seconds until the user hung up. `connectionState` aggregates the ICE
  // transport and the DTLS transport; the ICE half was connected, so what had not
  // finished was the DTLS handshake — and NONE of the four states above names it.
  // The diagnosis had to be inferred, and a failure captured after this must not
  // need inference.
  //
  // `RTCDtlsTransport.state` is reachable synchronously off a sender, and
  // `getStats()` adds the two fields that actually discriminate: `dtlsRole`
  // (a handshake where both ends chose the same role never completes) and the
  // selected candidate pair. Both are read here, and both are LOGGING ONLY — the
  // whole helper is behind `callDiagEnabled()` and is a no-op with the flag off.
  const dtlsSync = (pc: RTCPeerConnection) => {
    try {
      for (const s of pc.getSenders()) if (s.transport) return s.transport.state;
      for (const r of pc.getReceivers()) if (r.transport) return r.transport.state;
    } catch { /* engine without .transport */ }
    return null;
  };
  const dumpTransport = async (peerId: number, pc: RTCPeerConnection) => {
    if (!callDiagEnabled()) return;
    try {
      const stats = await pc.getStats();
      // The `transport` stat's shape is not in the DOM lib, so it is DECLARED
      // rather than erased with a cast — `as any` is what the ratchet counts,
      // and a shape written out is refutable by the next engine that changes it.
      stats.forEach((r: TransportStat) => {
        if (r.type !== "transport") return;
        diagLog(`peer ${peerId} transport`, {
          dtlsState: r.dtlsState,
          dtlsRole: r.dtlsRole,
          iceState: r.iceState,
          selectedCandidatePairId: r.selectedCandidatePairId,
          srtpCipher: r.srtpCipher,
          tlsVersion: r.tlsVersion,
        });
      });
    } catch (e) { diagWarn(`peer ${peerId}: getStats() for the transport dump threw`, e); }
  };
  /* 73 §2 — WHAT WE ARE ACTUALLY SENDING, WHICH IS THE FIELD THAT SEPARATES TWO
     COMPLETELY DIFFERENT FAULTS. Brief 70 part four made the DTLS half visible,
     and every field it added describes the TRANSPORT. So a capture can now say
     "ICE connected, DTLS incomplete" — but it cannot yet distinguish that from a
     connection that came up perfectly and carries no media because our own
     sender never got a track. Both look identical on screen: a tile that never
     produces a frame. They have nothing else in common and no shared fix.
     `sender.track` is the discriminator, so it is printed: kind, readyState and
     muted, per sender, plus whether the sender is one the negotiation actually
     associated. Read with `directions` (which already prints
     `direction/currentDirection`) it says, in one line, which side of the wire
     the fault is on. */
  // Typed rather than `any`, on the same rule CallWindow's `userAgentData` sniff
  // and `TransportStat` above follow: the ratchet counts a written `any`, and a
  // shape written out is refutable by the next engine that changes it.
  type SenderPair = { senders: { audio: RTCRtpSender | null; video: RTCRtpSender | null } };
  const sendState = (peer: SenderPair) => {
    const one = (s: RTCRtpSender | null) => {
      if (!s) return "no-sender";
      const t = s.track;
      if (!t) return "no-track";
      return `${t.kind}:${t.readyState}${t.muted ? ":muted" : ""}${t.enabled ? "" : ":disabled"}`;
    };
    return { sendAudio: one(peer.senders.audio), sendVideo: one(peer.senders.video) };
  };
  const dumpPeers = () => {
    for (const peer of peers.values()) {
      const pc = peer.pc;
      dumpTransport(peer.id, pc);
      let tx: any[] = [];
      try {
        tx = pc.getTransceivers().map((x: any) => {
          const kind = (x.receiver && x.receiver.track && x.receiver.track.kind)
            || (x.sender && x.sender.track && x.sender.track.kind) || "?";
          return `${kind}:${x.direction}/${x.currentDirection || "-"}`;
        });
      } catch { /* engine without getTransceivers */ }
      diagLog(`peer ${peer.id} state`, {
        signalingState: pc.signalingState,
        iceConnectionState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
        connectionState: pc.connectionState,
        dtlsState: dtlsSync(pc), // 70 §11bis — the half that was stuck and unprinted
        stalled: !!peer.stalled,
        transceivers: tx.length,
        directions: tx.join(", "),
        ontrackFired: !!peer.ontrackFired,
        armed: !!peer.armed,
        ...sendState(peer), // 73 §2 — our side of the wire
        localMlines: mLineCount(pc.localDescription),
        remoteMlines: mLineCount(pc.remoteDescription),
      });
    }
  };

  function getState() {
    refreshSentMirror(); // 74o (a) — before anything reads the snapshot
    return {
      callId,
      sentStream: sentMirror,
      micOn,
      camOn,
      localStream: stream,
      hasLocalVideo: !!(stream && stream.getVideoTracks().length),
      hasLocalAudio: !!(stream && stream.getAudioTracks().length),
      // 70 §11bis — `stalled` is the ICE-connected-but-never-completes case. It
      // is NOT derivable from `state`, which reads "connecting" for both a call
      // that is still setting up and one that has stopped setting up.
      // 73 §2 — `mediaSilent` is connected-and-carrying-nothing, which `state`
      // reads as "connected" and `stalled` does not cover. The UI needs both to
      // tell a person which of the two happened.
      peers: [...peers.values()].map((p) => ({
        id: p.id, stream: p.stream, state: p.state, armed: p.armed,
        stalled: !!p.stalled, mediaSilent: !!p.mediaSilent,
      })),
      sessionTag,
    };
  }

  // ── uplink ────────────────────────────────────────────────────────────────
  async function sendSignal(peerId: number, kind: string, payload: any) {
    const seq = (seqByPeer.get(peerId) || 0) + 1;
    seqByPeer.set(peerId, seq);
    const body = { to: peerId, kind, payload, seq };
    // The POST is the write to the durable mailbox: once it lands, delivery is
    // guaranteed (push + pull). Retry transient failures so a flaky uplink
    // can't drop negotiation.
    for (let attempt = 0; attempt < 4 && !destroyed; attempt++) {
      try { await api.post(`/calls/${callId}/signal`, body); return; }
      catch (e: any) {
        if (e && (e.code === "generic.forbidden" || e.code === "generic.not_found")) { diagWarn(`peer ${peerId}: sendSignal(${kind}) rejected ${e.code} — NOT delivered`); return; } // no longer in the call
        await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
      }
    }
    if (!destroyed) diagWarn(`peer ${peerId}: sendSignal(${kind}) exhausted retries — NOT delivered`);
  }

  // ── downlink apply serialization ────────────────────────────────────────────
  // Every signal — whether applied straight off the push or fetched by the pull
  // — runs through this single chain, so two sources can't drive the same PC's
  // signalingState concurrently.
  function enqueueApply(from: number, kind: string, payload: any) {
    applyChain = applyChain.then(() => applySignal(from, kind, payload)).catch(() => {});
    return applyChain;
  }

  // ── downlink: durable BACKSTOP pull (dropped-push / reconnect / multi-proc) ──
  async function pull() {
    if (destroyed) return;
    if (pulling) { pullAgain = true; return; }
    pulling = true;
    try {
      const r = (await api.get(`/calls/${callId}/signals?after=${cursor}`)) as {
        signals?: { id: number | string; from: number | string; kind: string; payload: string }[];
      } | null;
      const sigs = (r && r.signals) || [];
      for (const s of sigs) {
        const id = Number(s.id);
        if (id > cursor) cursor = id; // advance the watermark in id order
        if (appliedIds.has(id)) { appliedIds.delete(id); continue; } // already push-applied
        let payload = null;
        try { payload = JSON.parse(s.payload); } catch { payload = null; }
        if (payload) await enqueueApply(Number(s.from), s.kind, payload);
      }
      // prune any push-applied ids now behind the watermark
      if (appliedIds.size) for (const id of [...appliedIds]) if (id <= cursor) appliedIds.delete(id);
    } catch { /* transient — the periodic pull retries */ }
    finally {
      pulling = false;
      if (pullAgain && !destroyed) { pullAgain = false; schedulePull(); }
    }
  }
  function schedulePull() {
    if (destroyed || pullTimer) return;
    pullTimer = setTimeout(() => { pullTimer = null; pull(); }, 40);
  }
  // App forwards SSE 'callSignal' pushes here. Phase 42 (Lever A): apply the
  // blob DIRECTLY (no GET). Dedupe against the pulled watermark + the push-ahead
  // set; do NOT advance `cursor` (the backstop pull owns it, contiguously). A
  // malformed/absent payload (older server) falls back to a durable pull.
  function onPush(p: any) {
    if (destroyed || !p || Number(p.callId) !== Number(callId)) return;
    const id = p.id != null ? Number(p.id) : null;
    if (id != null && (id <= cursor || appliedIds.has(id))) return; // already applied
    if (id != null && p.kind && p.payload != null) {
      // Phase 42 FIX — the ephemeral carries the SERVER-STRINGIFIED blob
      // (callService JSON.stringify's it, then sends it as `payload`); the SSE
      // client parses only the OUTER event, so p.payload is a STRING here. Parse
      // it exactly as the durable pull does. THIS was the ring-time deadlock: the
      // string was handed straight to applySignal (payload.description ===
      // undefined) → silent early-return, and the id was pre-marked applied so the
      // pull skipped it too. On parse failure now: do NOT mark it applied (leave
      // the row for the pull to recover) and log LOUD — no silent drop.
      let payload = p.payload;
      if (typeof payload === "string") { try { payload = JSON.parse(payload); } catch { payload = null; } }
      if (payload) {
        appliedIds.add(id);
        enqueueApply(Number(p.from), p.kind, payload);
        return;
      }
      console.warn(`[lxcall ${callId}] onPush: unparseable payload for signal #${id} (${p.kind}) from ${p.from} — left for the pull`);
    }
    schedulePull();
  }

  // ── per-peer connection ─────────────────────────────────────────────────────
  function ensurePeer(peerId: any) {
    peerId = Number(peerId);
    if (!peerId || peerId === self) return null;
    const existing = peers.get(peerId);
    if (existing) return existing;
    const pc = new RTCPeerConnection(config);
    callPerfMark(callId, "pc-created");
    const peer: any = {
      id: peerId, pc, stream: null, state: "connecting",
      polite: self > peerId, // higher id rolls back on glare
      makingOffer: false, ignoreOffer: false, pendingIce: [], failTimer: null,
      senders: { audio: null, video: null }, armed: false, wantArm: false, ontrackFired: false,
      stallTimer: null, stalled: false, iceRestarted: false,
      // 73 §2 — connected, and carrying no media. A DIFFERENT fault from
      // `stalled`, watched separately below.
      silentTimer: null, mediaSilent: false,
    };
    peers.set(peerId, peer);

    // Phase 42 (Lever C) — media-less sendrecv transceivers: negotiate sendable
    // audio+video m-lines with NO track, so media can be flipped on later via
    // replaceTrack WITHOUT renegotiation (ring-time pre-warm + reneg-free
    // accept). Tracks are injected ONLY by armPeer, never here — a pre-warmed PC
    // carries no media until the call is accepted (the privacy invariant). A
    // recvonly-equivalent (denied media) peer simply never gets a track.
    try {
      const txA = pc.addTransceiver("audio", { direction: "sendrecv" });
      const txV = pc.addTransceiver("video", { direction: "sendrecv" });
      peer.senders.audio = txA.sender;
      peer.senders.video = txV.sender;
    } catch { /* engine without addTransceiver — armPeer falls back to addTrack */ }

    pc.onnegotiationneeded = async () => {
      // Phase 42 FIX — designate the IMPOLITE (lower-id) peer as the SOLE offerer,
      // so ring-time pre-negotiation converges with no double-offer glare: the
      // POLITE (higher-id) side never offers — it only answers the incoming offer.
      // Both the caller (media-less offer at initiate) and the invitee (media-less
      // offer during ring) used to run this handler, leaving BOTH stuck in
      // 'have-local-offer' with neither answering. With one offerer there is no
      // collision, no rollback, no ufrag churn → deterministic 'stable' → ICE.
      // Media stays deferred (this only decides who offers; no track is added).
      if (peer.polite) return;
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        await sendSignal(peerId, "offer", { description: pc.localDescription });
        callPerfMark(callId, "offer-sent");
      } catch (e) { console.warn(`[lxcall ${callId}] peer ${peerId}: onnegotiationneeded (create/send offer) failed`, e); }
      finally { peer.makingOffer = false; }
    };
    pc.onicecandidate = ({ candidate }) => { if (candidate) sendSignal(peerId, "ice", { candidate }); };
    pc.ontrack = (ev) => {
      let ms = (ev.streams && ev.streams[0]) || peer.stream;
      if (!ms) { ms = new MediaStream(); }
      if (!(ev.streams && ev.streams[0])) { try { ms.addTrack(ev.track); } catch { /* ignore */ } }
      peer.stream = ms;
      peer.ontrackFired = true;
      clearSilent(); // 73 §2 — media arrived; whatever we were about to say is moot
      diagLog(`peer ${peer.id}: ontrack ${ev.track ? ev.track.kind : "?"}`);
      if (!perfTrackMarked) { perfTrackMarked = true; callPerfMark(callId, "remote-track"); }
      emit();
    };
    // ── 70 §11bis — A CONNECTION THAT REACHES ICE-CONNECTED AND STOPS ─────────
    // Nothing in this tree watched for it. The failure timer below arms only on
    // `connectionState === "failed"`, and a DTLS handshake that never completes
    // does NOT produce `failed` — it produces `connecting`, indefinitely. So on
    // 2026-08-11 a call sat at `connecting` for nine seconds with ICE connected,
    // no timer, no message and no retry, and the user had to hang up. Silence was
    // the whole of the defect on this side.
    //
    // THIS IS NOT A DIAGNOSIS AND IT MUST NOT BE READ AS ONE. Why the handshake
    // did not finish is NOT established (see the PR body for what the one-ended
    // capture can and cannot support). What is fixed here is that the stall stops
    // being silent: the person on the screen is told, and can act. That is worth
    // doing on its own merits and does not depend on the cause.
    //
    // FIVE SECONDS, and the number is measured rather than picked: the working
    // call in the same capture reached `ice-connected` 358.6 ms after initiate,
    // so five seconds is more than thirteen times a healthy setup, and half the
    // nine the user endured before giving up. It is deliberately NOT the 9,000 ms
    // below — that one removes a tile, and this one only speaks.
    //
    // The single `restartIce()` is a RECOVERY ATTEMPT, not a fix, and it is
    // deliberately once: a retry loop that made the symptom go away would destroy
    // the evidence for the cause, which is the failure this project's method
    // exists to prevent. If it does not recover, the peer stays `stalled` and
    // says so.
    const STALL_MS = 5000;
    const clearStall = () => {
      if (peer.stallTimer) { clearTimeout(peer.stallTimer); peer.stallTimer = null; }
      if (peer.stalled) { peer.stalled = false; emit(); }
    };
    const armStall = () => {
      if (peer.stallTimer || peer.stalled) return;
      if (pc.connectionState === "connected") return;
      peer.stallTimer = setTimeout(() => {
        peer.stallTimer = null;
        if (destroyed || pc.connectionState === "connected") return;
        // UNCONDITIONAL, like the two negotiation-critical warnings: a stall that
        // only speaks when a debug flag is on is the silence this fixes.
        console.warn(
          `[lxcall ${callId}] peer ${peerId}: ICE is ${pc.iceConnectionState} but connectionState is still `
          + `${pc.connectionState} after ${STALL_MS}ms — the DTLS half has not completed `
          + `(dtls=${dtlsSync(pc) || "unknown"}). Enable lx:callperf for the transport dump.`,
        );
        dumpTransport(peerId, pc);
        peer.stalled = true;
        if (!peer.iceRestarted) {
          peer.iceRestarted = true;
          try { pc.restartIce && pc.restartIce(); } catch { /* engine without restartIce */ }
          // 142 §2 — AND THE POLITE PEER CANNOT PERFORM ITS OWN RESTART.
          // `restartIce()` only arms a new ufrag and fires `negotiationneeded`;
          // the OFFER is what performs the restart, and the polite (higher-id)
          // peer returns from that handler immediately (:482). So this recovery
          // was dead on the higher-id side of every pair. RULED (b): signal it
          // instead of offering it — the impolite peer restarts on receipt and
          // re-offers through the path it already owns, so Phase 42's
          // sole-offerer rule is never broken and no glare is re-admitted.
          if (peer.polite) sendSignal(peerId, "restart", {});
        }
        emit();
      }, STALL_MS);
    };
    // The ICE half completing is the transition that arms it, and it has never
    // had a handler — `oniceconnectionstatechange` is where "the ICE half is done"
    // is observable. `onconnectionstatechange` cannot serve: in the captured
    // failure it fired ONCE, for `connecting`, and never again.
    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      diagLog(`peer ${peerId}: iceConnectionState=${s} connectionState=${pc.connectionState}`);
      if (s === "connected" || s === "completed") armStall();
      else if (s === "closed") clearStall();
    };
    /* ── 73 §2 — A CONNECTION THAT COMES UP AND CARRIES NOTHING ────────────────
       Brief 70 part four watched the transport: ICE connected, `connectionState`
       still `connecting`, DTLS incomplete. Every field it added describes that.
       It cannot see the OTHER way a tile never produces a frame — a PC that
       reaches `connected` cleanly and delivers no media, because the far end's
       sender never got a track or its answer never committed to sending. On
       screen the two are indistinguishable; in cause and in fix they have
       nothing in common, and 73 §1 is the first report where telling them apart
       is the whole question.
       SIX SECONDS FROM `connected`, and the number is derived from the one
       measurement this tree has rather than picked: brief 70's capture showed a
       healthy call reaching `ice-connected` 358.6 ms after initiate, and media
       follows within a frame interval of that. Six seconds is more than an order
       of magnitude past healthy and short enough that the person on the screen
       has not yet given up. It is deliberately longer than the 5 s stall watch,
       so a connection that is BOTH stalled and silent reports the stall first —
       the transport fault is the one that explains the other.
       UNCONDITIONAL, like the stall warning, and for the same reason: a
       diagnosis that only speaks when a debug flag is already on cannot be read
       off the first failure. It names both discriminators — the transport state
       AND what our own senders hold — so one line says which side of the wire to
       look at. */
    const SILENT_MS = 6000;
    const clearSilent = () => {
      if (peer.silentTimer) { clearTimeout(peer.silentTimer); peer.silentTimer = null; }
      if (peer.mediaSilent) { peer.mediaSilent = false; emit(); }
    };
    const armSilent = () => {
      if (!expectRemoteMedia || peer.silentTimer || peer.mediaSilent || peer.ontrackFired) return;
      peer.silentTimer = setTimeout(() => {
        peer.silentTimer = null;
        if (destroyed || peer.ontrackFired) return;
        console.warn(
          `[lxcall ${callId}] peer ${peerId}: connectionState=${pc.connectionState} `
          + `(ice=${pc.iceConnectionState}, dtls=${dtlsSync(pc) || "unknown"}) but NO remote track after `
          + `${SILENT_MS}ms — the connection is up and no media is arriving. This is NOT the 70 §11bis `
          + `DTLS stall; look at what the FAR end is sending.`,
          sendState(peer),
        );
        dumpTransport(peerId, pc);
        peer.mediaSilent = true;
        emit();
      }, SILENT_MS);
    };
    pc.onconnectionstatechange = () => {
      peer.state = pc.connectionState;
      diagLog(`peer ${peerId}: connectionState=${pc.connectionState} ice=${pc.iceConnectionState}`);
      if (pc.connectionState === "connected") armSilent();
      if (pc.connectionState === "closed" || pc.connectionState === "failed") clearSilent();
      if (pc.connectionState === "connected" || pc.connectionState === "closed") clearStall();
      // 2026-07-13 fix pass — "completed" is an ICE state, never a
      // connectionState value; the old compare (masked by a B.1-era cast)
      // could not match. Read iceConnectionState for that leg.
      if ((pc.connectionState === "connected" || pc.iceConnectionState === "completed") && !perfIceMarked) {
        perfIceMarked = true; callPerfMark(callId, "ice-connected");
      }
      if (pc.connectionState === "failed") {
        try { pc.restartIce && pc.restartIce(); } catch { /* ignore */ }
        // 142 §2 — the second dead recovery path, same reason as the stall one
        // above and the same fix. Unguarded, exactly like the local call it
        // accompanies: this mirrors what that line already does per transition.
        if (peer.polite) sendSignal(peerId, "restart", {});
        // Ungraceful drop (no 'leave' arrives): if it doesn't recover, drop the
        // tile so the window doesn't keep a dead peer forever.
        if (!peer.failTimer) {
          peer.failTimer = setTimeout(() => {
            if (peer.pc.connectionState !== "connected") removePeer(peerId);
          }, 9000);
        }
      } else if (peer.failTimer) {
        clearTimeout(peer.failTimer); peer.failTimer = null;
      }
      emit();
    };
    emit();
    return peer;
  }

  // Inject the current local tracks into a peer's pre-created senders. replaceTrack
  // is transparent to the far end (no renegotiation) when the transceiver is
  // already sendrecv — this is what makes accept a pure "streams on" flip.
  /* 73 §2 — `replaceTrack` RETURNS A PROMISE, so the synchronous `try/catch` this
     used to rely on caught nothing: a rejection escaped as an unhandled rejection
     and `armPeer` set `armed = true` regardless. A peer could therefore be armed,
     believed to be sending, and sending nothing, with no line anywhere saying so
     — the exact silence brief 70 part four spent a cycle paying for. The call is
     unchanged (still not awaited, so arming stays synchronous and reneg-free);
     what is added is that the failure SPEAKS. `addTrack` is synchronous and its
     `catch` was already real. */
  function applyTracksToPeer(peer: any) {
    if (!stream) return;
    const a = stream.getAudioTracks()[0] || null;
    const v = stream.getVideoTracks()[0] || null;
    const loud = (kind: string) => (e: unknown) => console.warn(
      `[lxcall ${callId}] peer ${peer.id}: replaceTrack(${kind}) REJECTED — this peer is armed and is not sending ${kind}`, e,
    );
    if (peer.senders.audio) { try { Promise.resolve(peer.senders.audio.replaceTrack(a)).catch(loud("audio")); } catch (e) { loud("audio")(e); } }
    else if (a) { try { peer.pc.addTrack(a, stream); } catch (e) { loud("audio")(e); } }
    if (peer.senders.video) { try { Promise.resolve(peer.senders.video.replaceTrack(v)).catch(loud("video")); } catch (e) { loud("video")(e); } }
    else if (v) { try { peer.pc.addTrack(v, stream); } catch (e) { loud("video")(e); } }
  }
  // Mark a peer as one we should send to, and flip media on if we already have it.
  function armPeer(peer: any) {
    peer.wantArm = true;
    if (peer.armed || !stream) return;
    applyTracksToPeer(peer);
    peer.armed = true;
    callPerfMark(callId, "media-armed");
  }

  async function applySignal(from: number, kind: string, payload: any) {
    if (destroyed) return;
    // Phase 42 — belt to onPush's parse: accept a still-stringified payload so a
    // type mismatch can never again silently swallow a signal. Loud on a genuine
    // null/unparseable payload or a peer that can't be set up, instead of the old
    // silent drop that hid the deadlock.
    if (typeof payload === "string") { try { payload = JSON.parse(payload); } catch { payload = null; } }
    if (!payload) { console.warn(`[lxcall ${callId}] applySignal(${kind}) from ${from}: null/unparseable payload — dropped`); return; }
    const peer = ensurePeer(from);
    if (!peer) { console.warn(`[lxcall ${callId}] applySignal(${kind}) from ${from}: no peer (self=${self}) — dropped`); return; }
    const pc = peer.pc;
    try {
      if (kind === "offer" || kind === "answer") {
        const desc = payload.description;
        if (!desc) return;
        const collision = desc.type === "offer" && (peer.makingOffer || pc.signalingState !== "stable");
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;
        await pc.setRemoteDescription(desc); // implicit rollback for the polite peer on glare
        if (desc.type === "answer") callPerfMark(callId, "answer-applied");
        const queued = peer.pendingIce; peer.pendingIce = [];
        for (const c of queued) { try { await pc.addIceCandidate(c); } catch (e) { diagWarn(`peer ${from}: addIceCandidate (queued flush) threw`, e); } }
        if (desc.type === "offer") {
          // Phase 42 FIX — the recipient answers with NO local track (privacy:
          // camera deferred to accept). A null-track transceiver can answer
          // 'recvonly', leaving NO sending path for the accept-time replaceTrack —
          // the recipient's video then never reaches the caller (the caller side
          // works because its OFFER already committed to sendrecv). Before
          // answering: force each associated transceiver to sendrecv so the answer
          // commits to SENDING, and RE-BIND our stored senders to the ACTUAL
          // associated transceivers (setRemoteDescription may associate ones other
          // than the pre-created senders). No track is attached and no device is
          // opened here — a sendrecv transceiver with a null track still sends
          // nothing (the privacy invariant holds); replaceTrack at accept then has
          // a real sending transceiver to flip onto, reneg-free.
          try {
            for (const tx of pc.getTransceivers()) {
              const k = tx.receiver && tx.receiver.track ? tx.receiver.track.kind : null;
              if (!k) continue;
              try { if (tx.direction !== "sendrecv") tx.direction = "sendrecv"; } catch { /* ignore */ }
              if (k === "audio") peer.senders.audio = tx.sender;
              else if (k === "video") peer.senders.video = tx.sender;
            }
          } catch { /* engine without getTransceivers — the addTransceiver senders stand */ }
          /* ── 73 §2 — THE HAZARD THE PARAGRAPH ABOVE NAMES, CHECKED RATHER THAN
             TRUSTED, AND IT IS A CANDIDATE CAUSE FOR THE DIRECTIONAL STALL.
             The re-bind above exists because "setRemoteDescription may associate
             ones other than the pre-created senders" — the code's own words. In
             the case it was written for that is harmless, because the CALLEE
             attaches its tracks LATER, at accept, onto whatever this loop bound.
             The order is: associate → re-bind → attach.
             A SHARE LEG IS THE FIRST CALLER WITH THE OPPOSITE ORDER. Its sharer
             holds media from the moment the session is built, so `armPeer` has
             already put the display track on the PRE-CREATED sender before any
             offer arrives: attach → associate → re-bind. If this loop then binds
             a different sender, the track is left on one the answer does not
             commit to sending and the new one holds nothing — a connection that
             comes up perfectly and delivers no picture, in ONE DIRECTION ONLY:
             the sharer who ANSWERS. The sharer who OFFERS is unaffected, because
             its offer was generated from the sender that already had the track.
             That is the shape 73 §1 reports.
             ── CONFIRMED 2026-08-12, CAPTURED ON TWO MACHINES (register 73a) ──
             This paragraph used to end "IT IS NOT CLAIMED AS THE CAUSE". It is
             claimed now, because it was reproduced: the assertion below fired
             with `{sendAudio: 'no-track', sendVideo: 'no-track'}` on calls 239,
             240, 243, 245 and 247 — **deterministic on the answering side, never
             intermittent** — and the repair carried the picture through
             (accept-click → first-remote-frame 238 ms on call 246).
             AND THE PRECONDITION WAS MEASURED, which is the half that explains
             the asymmetry, from the sharer's own timeline:
               conversation (246): remote-track 163 ms → media-armed 5476 ms
                                   media attaches AFTER the answer
               share leg   (247): media-armed 2.6 ms → remote-track 225 ms
                                   media attaches BEFORE the answer
             So it fails only where media is armed BEFORE answering: every share
             leg, no conversation. Same code, opposite order — which is exactly
             what the paragraph above predicted, and the numbers are kept here
             because a future reader deserves the evidence and not the conclusion.
             IT IS NOT 70 §11bis's STALL, and that was settled by the same
             capture: every transport dump read `dtlsState: 'connected'` with
             `dtlsRole` client on the answerer and server on the offerer — correct
             opposite roles, a completed handshake. Two candidates raised earlier
             were also refuted: the Chrome version difference and
             machine/environment settings. The fault was in this code.
             SO THE LOG IS NOW GATED WHERE IT IS EXPECTED, and that is a change of
             volume rather than of behaviour. The condition holds on EVERY share
             leg by construction, so an unconditional warning would fire on every
             correct share for ever — which is how a channel stops being read. It
             stays UNCONDITIONAL where it would still be news: a session that
             expects remote media is a conversation, and a conversation arming
             before it answers has never been observed. The repair itself is
             unconditional either way, and the far side's `mediaSilent` watch is
             still the loud net if it ever stops working. */
          if (peer.armed && stream) {
            const want = { audio: stream.getAudioTracks()[0] || null, video: stream.getVideoTracks()[0] || null };
            const lost = (["audio", "video"] as const).filter(
              (k) => want[k] && peer.senders[k] && peer.senders[k].track !== want[k],
            );
            if (lost.length) {
              const say = expectRemoteMedia ? console.warn : diagLog;
              say(
                `[lxcall ${callId}] peer ${from}: answering an offer moved ${lost.join("+")} off the armed `
                + `sender(s) — the transceiver re-bind picked a sender that does not hold our track. `
                + `Re-attaching before the answer (register 73a — the named cause, reproduced 2026-08-12).`
                + (expectRemoteMedia ? " THIS SESSION EXPECTS REMOTE MEDIA, so it is a CONVERSATION arming before it answers — never observed before; look at it." : ""),
                sendState(peer),
              );
              applyTracksToPeer(peer);
            }
          }
          await pc.setLocalDescription();
          await sendSignal(from, "answer", { description: pc.localDescription });
        }
      } else if (kind === "ice") {
        const c = payload.candidate;
        if (!c) return;
        if (!pc.remoteDescription || !pc.remoteDescription.type) { peer.pendingIce.push(c); return; }
        try { await pc.addIceCandidate(c); } catch (e) { diagWarn(`peer ${from}: addIceCandidate threw (may be benign — candidate for an ignored/rolled-back offer)`, e); }
      } else if (kind === "restart") {
        // 142 §2 (b) — the far side asked for an ICE restart it is not allowed
        // to perform itself. Restarting here fires `onnegotiationneeded`, which
        // THIS (impolite) peer already honours, so the re-offer goes out through
        // the existing sole-offerer path. No new offer path, no glare.
        try { pc.restartIce && pc.restartIce(); } catch { /* engine without restartIce */ }
      } else if (kind === "bye") {
        removePeer(from);
      }
    } catch (e) { console.warn(`[lxcall ${callId}] peer ${from}: applySignal(${kind}) failed (signalingState=${pc.signalingState})`, e); }
  }

  function removePeer(peerId: any) {
    peerId = Number(peerId);
    const peer = peers.get(peerId);
    if (!peer) return;
    if (peer.failTimer) clearTimeout(peer.failTimer);
    if (peer.stallTimer) clearTimeout(peer.stallTimer); // 70 §11bis
    if (peer.silentTimer) clearTimeout(peer.silentTimer); // 73 §2
    try { peer.pc.close(); } catch { /* ignore */ }
    peers.delete(peerId);
    emit();
  }

  // Reconcile the mesh to the authoritative roster (App computes it from the
  // call row). Phase 42: MESH ids get a media-less PC (incl. ringing invitees
  // for pre-warm); ARMED ids additionally get our local tracks (only peers both
  // ends have committed to). A peer is never disarmed — it only leaves via
  // removePeer when it drops out of the mesh.
  function setPeers(meshIds?: any[], armedIds?: any[]) {
    if (destroyed) return;
    const want = new Set((meshIds || []).map(Number).filter((n) => n && n !== self));
    const armed = new Set((armedIds || []).map(Number).filter((n) => n && n !== self));
    for (const id of want) ensurePeer(id);
    for (const id of [...peers.keys()]) if (!want.has(id)) removePeer(id);
    for (const peer of peers.values()) if (armed.has(peer.id)) armPeer(peer);
    emit();
  }

  // Phase 42 — the accept leg (or a caller who joined media-less) supplies the
  // local stream now; flip it into every peer already marked to send. No
  // renegotiation (replaceTrack on the pre-created sendrecv transceivers).
  function setLocalMedia(newStream: MediaStream | null) {
    if (destroyed) return;
    stream = newStream || null;
    micOn = !!(stream && stream.getAudioTracks().some((t) => t.enabled));
    camOn = !!(stream && stream.getVideoTracks().some((t) => t.enabled));
    for (const peer of peers.values()) {
      if (peer.wantArm && !peer.armed) {
        applyTracksToPeer(peer);
        peer.armed = true;
        callPerfMark(callId, "media-armed");
      }
    }
    emit();
  }

  function toggleMic() {
    if (!stream || !stream.getAudioTracks().length) return micOn;
    micOn = !micOn;
    stream.getAudioTracks().forEach((t) => { t.enabled = micOn; });
    emit();
    return micOn;
  }
  function toggleCam() {
    if (!stream || !stream.getVideoTracks().length) return camOn;
    camOn = !camOn;
    stream.getVideoTracks().forEach((t) => { t.enabled = camOn; });
    emit();
    return camOn;
  }

  // Phase 39 (Corr) — swap the local mic/camera mid-call (the call-window device
  // cog). Replace the track on every existing sender (no renegotiation needed —
  // replaceTrack is transparent to the far end); addTrack for a kind that had no
  // sender yet. The new tracks inherit the current mute state, then the old
  // stream is stopped so the device is released.
  async function replaceLocalStream(newStream: MediaStream | null) {
    if (destroyed || !newStream) return;
    const old = stream;
    const newAudio = newStream.getAudioTracks()[0] || null;
    const newVideo = newStream.getVideoTracks()[0] || null;
    if (newAudio) newAudio.enabled = micOn;
    if (newVideo) newVideo.enabled = camOn;
    for (const peer of peers.values()) {
      if (peer.senders.audio) { try { await peer.senders.audio.replaceTrack(newAudio); } catch { /* ignore */ } }
      else if (newAudio) { try { peer.pc.addTrack(newAudio, newStream); } catch { /* ignore */ } }
      if (peer.senders.video) { try { await peer.senders.video.replaceTrack(newVideo); } catch { /* ignore */ } }
      else if (newVideo) { try { peer.pc.addTrack(newVideo, newStream); } catch { /* ignore */ } }
      peer.armed = true; peer.wantArm = true;
    }
    stream = newStream;
    micOn = !!(newAudio && newAudio.enabled);
    camOn = !!(newVideo && newVideo.enabled);
    if (old && old !== newStream) { try { old.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } }
    emit();
  }

  async function hangup() {
    try { await api.post(`/calls/${callId}/leave`, {}); } catch { /* ignore */ }
    destroy();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    callPerfFlush(callId, "session-destroyed"); // no-op unless a run is still open
    if (pullTimer) clearTimeout(pullTimer);
    if (periodic) clearInterval(periodic);
    for (const peer of peers.values()) {
      if (peer.failTimer) clearTimeout(peer.failTimer);
      if (peer.stallTimer) clearTimeout(peer.stallTimer);
      if (peer.silentTimer) clearTimeout(peer.silentTimer);
      try { peer.pc.close(); } catch { /* ignore */ }
    }
    peers.clear();
    if (stream) { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } }
    stream = null;
  }

  // Periodic pull backstop: frequent while any peer is still settling (covers
  // dropped pushes / reconnect windows + advances the cursor and prunes the
  // push-ahead set), then a slow heartbeat once connected.
  const periodic = setInterval(() => {
    if (destroyed) return;
    ticks++;
    if (callDiagEnabled()) dumpPeers();
    // 2026-07-13 fix pass — same mental-model bug as the perf-mark site:
    // p.state caches pc.connectionState, which is never "completed" (an ICE
    // state), so that leg was dead. Read the live iceConnectionState.
    const unsettled = [...peers.values()].some(
      (p) => p.state !== "connected" && p.pc.iceConnectionState !== "completed",
    );
    if (unsettled || ticks % 5 === 0) pull();
  }, 2000);
  // Catch any signal that beat this session into existence.
  pull();

  return {
    callId,
    getState,
    onPush,
    pull,
    setPeers,
    setLocalMedia,
    toggleMic,
    toggleCam,
    replaceLocalStream,
    hangup,
    destroy,
    get micOn() { return micOn; },
    get camOn() { return camOn; },
    get peerCount() { return peers.size; },
  };
}
